import React, { useState, useRef, useEffect, useMemo } from 'react';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import { useDispatch, useSelector } from 'react-redux';
import { setUserDetails } from "../../Redux/Slices/userDetailsSlice";
import { getToken, getUserId } from "../../api/index";
import { updateUser } from "../../api/auth";
import Loader from '../Loader';
import { Snackbar, Alert } from '@mui/material';
import { POLITICAL_PARTIES } from '../../constants/politicalParties';
import './EditPoliticalAffiliation.css';

const EditPoliticalAffiliation = () => {
    const dispatch = useDispatch();
    const userDetails = useSelector((state) => state.userDetails.userDetails);
    const [menuOpen, setMenuOpen] = useState(false);
    const [selectedParty, setSelectedParty] = useState(userDetails?.politicalAffiliation || "");
    const [searchQuery, setSearchQuery] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    useEffect(() => {
        if (userDetails?.politicalAffiliation) {
            setSelectedParty(userDetails.politicalAffiliation);
        }
    }, [userDetails]);

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar({ ...snackbar, open: false });
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const filteredParties = useMemo(() => {
        if (!searchQuery.trim()) return POLITICAL_PARTIES;
        const query = searchQuery.toLowerCase().trim();
        return POLITICAL_PARTIES.filter(party => party.toLowerCase().includes(query));
    }, [searchQuery]);

    const handleSelectParty = (party) => {
        setSelectedParty(party);
        setMenuOpen(false);
        setSearchQuery("");
    };

    const handleClearSelection = (e) => {
        e.stopPropagation();
        setSelectedParty("");
    };

    const toggleDropdown = () => {
        const nextState = !menuOpen;
        setMenuOpen(nextState);
        if (nextState) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleSubmit = async () => {
        if (isLoading) return;
        setIsLoading(true);
        const token = getToken();
        const payload = {
            userId: getUserId(),
            politicalAffiliation: selectedParty || null,
        };

        try {
            const response = await updateUser(payload, token);
            console.log("updateUser political affiliation response:", response);

            if (response && response.success !== false) {
                const msg = selectedParty
                    ? `Political affiliation updated! The ${selectedParty} Community is now unlocked for you.`
                    : 'Political affiliation updated successfully!';
                showSnackbar(msg, 'success');
                dispatch(setUserDetails({ ...userDetails, politicalAffiliation: selectedParty }));
            } else {
                showSnackbar('Failed to update political affiliation. Please try again.', 'error');
            }
        } catch (error) {
            console.error("Error updating political affiliation:", error);
            showSnackbar('An error occurred while updating. Please try again.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="EditPoliticalAffiliation">
            <div className="editPoliticalAffiliationContainer">
                <div className="editPoliticalAffiliationCard">
                    <div className="editPoliticalHeader">
                        <span className="editPoliticalLabel">Political affiliation</span>
                        <span className="politicalSupportPrompt">I currently support the:</span>
                    </div>

                    <div className="politicalDropdownWrapper" ref={dropdownRef}>
                        <div
                            id="political-affiliation-select"
                            className="politicalSelectBox"
                            onClick={toggleDropdown}
                        >
                            <span className={selectedParty ? "selectedPartyText" : "placeholderPartyText"}>
                                {selectedParty ? `I currently support the ${selectedParty}` : "Select a political party"}
                            </span>

                            <div className="politicalSelectActions">
                                {selectedParty && (
                                    <span
                                        className="clearPartyBtn"
                                        onClick={handleClearSelection}
                                        title="Clear selection"
                                    >
                                        <ClearRoundedIcon sx={{ fontSize: 16 }} />
                                    </span>
                                )}
                                <span className="politicalArrowIcon">
                                    {menuOpen ? (
                                        <KeyboardArrowUpRoundedIcon />
                                    ) : (
                                        <KeyboardArrowDownRoundedIcon />
                                    )}
                                </span>
                            </div>
                        </div>

                        {menuOpen && (
                            <div className="politicalDropdownMenu">
                                <div className="politicalSearchBox" onClick={(e) => e.stopPropagation()}>
                                    <SearchRoundedIcon sx={{ fontSize: 18, color: '#5AAF9A', marginRight: '8px' }} />
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        className="partySearchInput"
                                        placeholder="Search political parties..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    {searchQuery && (
                                        <ClearRoundedIcon
                                            sx={{ fontSize: 16, color: '#839696', cursor: 'pointer' }}
                                            onClick={() => setSearchQuery('')}
                                        />
                                    )}
                                </div>

                                <ul className="partyList">
                                    {filteredParties.length > 0 ? (
                                        filteredParties.map((party) => (
                                            <li
                                                key={party}
                                                className={`partyItem ${selectedParty === party ? 'active' : ''}`}
                                                onClick={() => handleSelectParty(party)}
                                            >
                                                <span>{party}</span>
                                                {selectedParty === party && (
                                                    <span className="selectedBadge">Selected</span>
                                                )}
                                            </li>
                                        ))
                                    ) : (
                                        <li className="noPartiesFound">
                                            No political parties matching "{searchQuery}"
                                        </li>
                                    )}
                                </ul>
                            </div>
                        )}
                    </div>

                    {userDetails?.politicalAffiliation && selectedParty && userDetails.politicalAffiliation.toLowerCase() !== selectedParty.toLowerCase() ? (
                        <div className="politicalCommunityNotice notice-warning">
                            <span className="noticeIcon">⚠️</span>
                            <span className="noticeText">
                                Switching from <strong>{userDetails.politicalAffiliation}</strong> to <strong>{selectedParty}</strong> will automatically remove you from the <strong>{userDetails.politicalAffiliation}</strong> community space and unlock the official <strong>{selectedParty}</strong> circle.
                            </span>
                        </div>
                    ) : userDetails?.politicalAffiliation && !selectedParty ? (
                        <div className="politicalCommunityNotice notice-warning">
                            <span className="noticeIcon">⚠️</span>
                            <span className="noticeText">
                                Clearing your political party will remove your membership and access from the <strong>{userDetails.politicalAffiliation}</strong> community space.
                            </span>
                        </div>
                    ) : selectedParty ? (
                        <div className="politicalCommunityNotice notice-info">
                            <span className="noticeIcon">🗳️</span>
                            <span className="noticeText">
                                Selecting <strong>{selectedParty}</strong> will unlock access to its dedicated <strong>Amalgamate Community</strong> space.
                            </span>
                        </div>
                    ) : (
                        <div className="politicalCommunityNotice notice-info">
                            <span className="noticeIcon">🗳️</span>
                            <span className="noticeText">
                                Selecting your political party automatically creates & unlocks access to its dedicated <strong>Amalgamate Community</strong> space.
                            </span>
                        </div>
                    )}
                </div>

                <div className="editPoliticalSubmitBtnContainer">
                    <button
                        className="editPoliticalSubmitBtn"
                        onClick={handleSubmit}
                        disabled={isLoading}
                    >
                        {isLoading ? <Loader /> : "Update"}
                    </button>
                </div>
            </div>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default EditPoliticalAffiliation;
